import { FC, ReactElement, useState } from 'react'
import { match } from 'ts-pattern'

import {
  Text,
  GreyGrey80,
  Primary13Regular,
  TextAreaFieldInput,
  useNotifications,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { ReviewOutcome } from '../../../types'
import { useNavigate } from 'react-router-dom'

export const ModalQuoteApprovalResponse: FC<{
  trigger: ReactElement
  outcome: ReviewOutcome
  completeTask: (outcome: any) => Promise<boolean>
  quoteNumber: string
  quoteId: string
}> = ({ trigger, outcome, completeTask, quoteNumber, quoteId }) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()

  const [comment, setComment] = useState<string>('')

  const {
    title,
    verb,
    completedVerb,
    notificationType,
    submitButtonText,
    submittingButtonText,
    body,
    isDestructive
  } = match(outcome)
    .with('APPROVED', () => ({
      title: 'Approve quote',
      submitButtonText: 'Approve quote',
      submittingButtonText: 'Approving quote...',
      body: `You are about to approve ${quoteNumber}. Once approved, this quote will be published.`,
      isDestructive: false,
      verb: 'approve',
      completedVerb: 'approved',
      notificationType: 'success' as const
    }))
    .with('REJECTED', () => ({
      title: 'Reject quote',
      submitButtonText: 'Reject quote',
      submittingButtonText: 'Rejecting quote...',
      body: `You are about to reject ${quoteNumber}. This quote will remain as a draft.`,
      isDestructive: true,
      verb: 'reject',
      completedVerb: 'rejected',
      notificationType: 'error' as const
    }))
    .exhaustive()

  return (
    <SimpleModal
      variant="dialog"
      trigger={trigger}
      title={title}
      onSubmit={async () => {
        const res = await completeTask({ outcome, comment })

        if (!res) {
          displayNotification(`Unable to ${verb} quote. Please try again.`, {
            type: 'error'
          })
          return false
        }

        displayNotification(`${quoteNumber} ${completedVerb}`, {
          type: notificationType,
          viewLabel: 'View quote',
          onClickView: () => navigate(`/quotes/${quoteId}`),
          'data-testid': 'watchtower.quoteApproval.toast'
        })

        return true
      }}
      submitButtonText={submitButtonText}
      submittingButtonText={submittingButtonText}
      isDestructive={isDestructive}
      disableSubmitOnEnter
      dialogPosition="top"
    >
      <Flex flexDir="column" gap="16px">
        <Text
          {...Primary13Regular}
          color={GreyGrey80}
          data-testid="watchtower.quoteApproval.body"
        >
          {body}
        </Text>

        <TextAreaFieldInput
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Optional message..."
          height="64px"
          data-testid="watchtower.quoteApproval.comment"
        />
      </Flex>
    </SimpleModal>
  )
}
